import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface TenantContextResponse {
  success: boolean;
  tenant_id?: string;
  error?: string;
  message?: string;
}

export const setTenantContext = async (tenantId: string | null): Promise<boolean> => {
  if (!tenantId) {
    await clearTenantContext();
    return true;
  }

  try {
    const { data, error } = await supabase.rpc('set_tenant_context', { p_tenant_id: tenantId });

    if (error) {
      console.error('Failed to set tenant context:', error.message);
      return false;
    }

    const response = data as TenantContextResponse;
    if (!response?.success) {
      console.error('Tenant context denied:', response?.error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error setting tenant context:', err);
    return false;
  }
};

export const clearTenantContext = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('clear_tenant_context');

    if (error) {
      console.error('Failed to clear tenant context:', error.message);
      return false;
    }

    const response = data as TenantContextResponse;
    return response?.success ?? false;
  } catch (err) {
    console.error('Error clearing tenant context:', err);
    return false;
  }
};

export const ensureValidSession = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const expiresAt = session.expires_at ?? 0;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const bufferSeconds = 30;

    if (expiresAt - nowSeconds < bufferSeconds) {
      const { data: { session: refreshed } } = await supabase.auth.refreshSession();
      return !!refreshed;
    }

    return true;
  } catch {
    return false;
  }
};

export const getCurrentTenantId = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase.rpc('get_current_tenant_id');

    if (error) {
      console.error('Failed to get current tenant ID:', error.message);
      return null;
    }

    return data as string | null;
  } catch (err) {
    console.error('Error getting current tenant ID:', err);
    return null;
  }
};
